import { Component, inject, OnInit } from '@angular/core';
import {
  MaintenanceRequestDetails,
  MaintenanceRequestService,
  UpdateMaintenanceRequestModel,
} from '../../../core/services/maintenance-request-service';
import { ActivatedRoute } from '@angular/router';
import { DatePipe, NgStyle } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ImagePreviewDialog } from '../../../component/dialog/image-preview-dialog/image-preview-dialog';
import { AssignUserDialog } from '../../../component/dialog/assign-user-dialog/assign-user-dialog';
import { A11yModule } from '@angular/cdk/a11y';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-maintenance-request-details-page',
  imports: [DatePipe, MatButtonModule, A11yModule],
  templateUrl: './maintenance-request-details-page.html',
  styleUrl: './maintenance-request-details-page.scss',
})
export class MaintenanceRequestDetailsPage implements OnInit {
  private maintenanceRequestService = inject(MaintenanceRequestService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);

  activeTab: 'history' | 'logs' = 'logs';
  request: MaintenanceRequestDetails | null = null;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? Number(idParam) : null;

    if (!id || isNaN(id)) {
      console.error('Invalid or missing ID');
      return;
    }

    this.maintenanceRequestService.getById(id).subscribe({
      next: (response) => {
        this.request = response.data;
      },
      error: (err) => {
        console.error('Failed to load request', err);
      },
    });
  }

  openImage(url: string): void {
    this.dialog.open(ImagePreviewDialog, {
      data: { url },
      panelClass: 'image-dialog',
      maxWidth: '90vw',
      maxHeight: '90vh',
    });
  }

  getPriorityStyle(priority: string | undefined) {
    switch ((priority ?? '').toLowerCase()) {
      case 'low':
        return { background: '#22c55e', color: '#fff' };

      case 'medium':
        return { background: '#3b82f6', color: '#fff' };

      case 'high':
        return { background: '#f59e0b', color: '#fff' };

      case 'critical':
        return {
          background: '#dc2626',
          color: '#fff',
          animation: 'pulse 1s infinite',
        };

      default:
        return { background: '#6b7280', color: '#fff' };
    }
  }

  openAssignDialog(): void {
    this.maintenanceRequestService
      .getAuthorizedMaintenanceLogCreaters()
      .subscribe((users) => {
        const dialogRef = this.dialog.open(AssignUserDialog, {
          width: '400px',
          data: {
            users: users.data,
            selectedUserId: this.request?.assignedTo?.id ?? null,
          },
        });

        dialogRef.afterClosed().subscribe((selectedUserId) => {
          if (!selectedUserId || !this.request?.id) return;

          this.assignUser(selectedUserId);
        });
      });
  }

  assignUser(userId: number): void {
    const payload: UpdateMaintenanceRequestModel = {
      assignedToId: userId,
      status: this.request?.status ?? '',
      priority: this.request?.priority ?? '',
      updateNote: 'Assigned via UI',
    };

    this.maintenanceRequestService
      .updateRequest(this.request!.id, payload)
      .subscribe({
        next: () => {
          this.reloadRequest();
        },
      });
  }

  reloadRequest(): void {
    this.maintenanceRequestService
      .getById(this.request!.id)
      .subscribe((res) => {
        this.request = res.data;
      });
  }
}
